import React, { useState, useEffect, useCallback } from 'react';
import { BackofficeLayout } from '../../layouts/BackofficeLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { RefreshCw, TrendingUp, Activity, Send, Building2, ChevronRight, Mail, CreditCard, Clock, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../context/AuthContext';

const riskColors = {
  critical: 'text-red-400 bg-red-500/10 border-red-500/30',
  high: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
  elevated: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  low: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
};

function Stat({ label, value, sub, className = '' }) {
  return (
    <div className={`py-3 px-4 bg-slate-800/50 rounded-sm ${className}`}>
      <span className="text-2xl font-black text-slate-100 tabular-nums block" style={{ fontFamily: 'Barlow Condensed' }}>
        {value ?? '—'}
      </span>
      <p className="text-[9px] uppercase tracking-[0.12em] text-slate-500 mt-0.5">{label}</p>
      {sub && <p className="text-[8px] text-slate-600 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function FounderDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [batchFilter, setBatchFilter] = useState('all');
  const [batchLogs, setBatchLogs] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/founder/dashboard');
      setData(res.data);
    } catch {
      toast.error('Failed to load dashboard');
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { setRefreshing(true); load(); };

  const loadBatch = async (bid) => {
    setBatchFilter(bid);
    if (bid === 'all') { setBatchLogs(null); return; }
    try {
      const res = await api.get(`/founder/dashboard/outreach?batch_id=${encodeURIComponent(bid)}`);
      setBatchLogs(res.data.logs);
    } catch {
      toast.error('Failed to load batch');
    }
  };

  if (loading) {
    return (
      <BackofficeLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-5 h-5 animate-spin text-slate-500" />
        </div>
      </BackofficeLayout>
    );
  }

  const f = data?.funnel || {};
  const outreach = data?.outreach || {};
  const org = data?.org_health || {};
  const bids = data?.recent_bids || [];

  return (
    <BackofficeLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="founder-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-black text-slate-100 tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              DASHBOARD
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">Consolidated view — numbers only</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={refresh}
            disabled={refreshing}
            className="text-slate-400 hover:text-slate-200"
            data-testid="dashboard-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Section 1: Conversion Funnel */}
        <Card className="bg-slate-900 border-orange-500/15" data-testid="section-conversion-funnel">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-orange-400" />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-300">Conversion Funnel</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Total QuickChecks" value={f.total_checks} sub={`${f.checks_7d || 0} last 7d`} />
              <Stat label="Email Capture Rate" value={f.email_rate_pct != null ? `${f.email_rate_pct}%` : '—'} sub={`${f.email_captured || 0} captured`} />
              <Stat label="Payment Conversion" value={f.payment_rate_pct != null ? `${f.payment_rate_pct}%` : '—'} sub={`${f.payments || 0} paid / ${f.email_captured || 0} captured`} />
              <Stat label="Median Time to Convert" value={f.median_ttc_mins != null ? `${f.median_ttc_mins}m` : '—'} sub="minutes from check to payment" />
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Bid Activity */}
        <Card className="bg-slate-900 border-slate-800" data-testid="section-bid-activity">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-300">Recent Bid Activity</span>
              <Badge className="bg-slate-800 text-slate-500 border-slate-700 text-[9px] ml-auto">{bids.length} shown</Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bid-activity-table">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left text-[9px] uppercase tracking-wider text-slate-600 pb-2 pr-4">Time</th>
                    <th className="text-right text-[9px] uppercase tracking-wider text-slate-600 pb-2 pr-4">Contract</th>
                    <th className="text-right text-[9px] uppercase tracking-wider text-slate-600 pb-2 pr-4">Margin</th>
                    <th className="text-left text-[9px] uppercase tracking-wider text-slate-600 pb-2 pr-4">Risk</th>
                    <th className="text-center text-[9px] uppercase tracking-wider text-slate-600 pb-2 pr-4">Email</th>
                    <th className="text-center text-[9px] uppercase tracking-wider text-slate-600 pb-2">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map((b, i) => {
                    const risk = b.outputs?.risk_level || 'low';
                    const ts = b.timestamp ? new Date(b.timestamp) : null;
                    return (
                      <tr key={b.bid_id || i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                        <td className="py-2 pr-4 text-xs text-slate-400 tabular-nums whitespace-nowrap">
                          {ts ? `${ts.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} ${ts.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '—'}
                        </td>
                        <td className="py-2 pr-4 text-xs text-slate-300 text-right tabular-nums">
                          {b.inputs?.contract_value ? `£${Number(b.inputs.contract_value).toLocaleString('en-GB')}` : '—'}
                        </td>
                        <td className="py-2 pr-4 text-xs text-right tabular-nums">
                          <span className={risk === 'low' ? 'text-emerald-400' : risk === 'elevated' ? 'text-amber-400' : 'text-red-400'}>
                            {b.outputs?.margin_pct != null ? `${b.outputs.margin_pct}%` : '—'}
                          </span>
                        </td>
                        <td className="py-2 pr-4">
                          <Badge className={`text-[9px] px-1.5 py-0 ${riskColors[risk] || riskColors.low}`}>
                            {risk}
                          </Badge>
                        </td>
                        <td className="py-2 pr-4 text-center">
                          {b.behaviour?.email_captured
                            ? <Mail className="w-3.5 h-3.5 text-emerald-400 inline" />
                            : <span className="text-slate-700">—</span>}
                        </td>
                        <td className="py-2 text-center">
                          {b.behaviour?.payment_completed
                            ? <CreditCard className="w-3.5 h-3.5 text-orange-400 inline" />
                            : <span className="text-slate-700">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {bids.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-slate-600 text-xs py-6">No bids recorded yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Outreach */}
        <Card className="bg-slate-900 border-slate-800" data-testid="section-outreach">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-4">
              <Send className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-300">Outreach</span>
              {outreach.batch_ids?.length > 0 && (
                <div className="ml-auto w-48">
                  <Select value={batchFilter} onValueChange={loadBatch}>
                    <SelectTrigger className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-300" data-testid="batch-filter">
                      <SelectValue placeholder="Filter by batch" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="all" className="text-xs text-slate-300">All batches</SelectItem>
                      {outreach.batch_ids.map(bid => (
                        <SelectItem key={bid} value={bid} className="text-xs text-slate-300">{bid}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <Stat label="Sent" value={outreach.sent} />
              <Stat label="Delivered" value={outreach.delivered} />
              <Stat label="Failed" value={outreach.failed} />
            </div>
            {batchLogs && (
              <div className="mt-3 pt-3 border-t border-slate-800">
                <p className="text-[9px] uppercase tracking-wider text-slate-600 mb-2">Batch: {batchFilter}</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {batchLogs.map((log, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs py-1 px-2 rounded bg-slate-800/40">
                      {log.send_status === 'sent' ? <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" /> :
                       log.send_status === 'failed' ? <XCircle className="w-3 h-3 text-red-400 shrink-0" /> :
                       <Clock className="w-3 h-3 text-slate-500 shrink-0" />}
                      <span className="text-slate-400 truncate flex-1">{log.recipient_email}</span>
                      <span className="text-slate-600 text-[10px] shrink-0">{log.subject}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 4: Org Health */}
        <Card className="bg-slate-900 border-slate-800" data-testid="section-org-health">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-bold uppercase tracking-widest text-slate-300">Org Health</span>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <Stat label="Active Orgs" value={org.active_orgs} />
              <Stat label="Single / Free" value={org.single_count} sub="£9 one-off" />
              <Stat label="Pro" value={org.pro_count} sub="£149/mo" />
            </div>
            {org.seat_usage?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-800">
                <p className="text-[9px] uppercase tracking-wider text-slate-600 mb-2">Pro Seat Usage</p>
                <div className="space-y-1.5">
                  {org.seat_usage.map((s, i) => (
                    <div key={i} className="flex items-center gap-3 text-xs py-1.5 px-2 rounded bg-slate-800/40">
                      <span className="text-slate-300 font-medium truncate flex-1">{s.name}</span>
                      <span className="tabular-nums text-slate-400">
                        {s.seats_used}<span className="text-slate-600">/{s.seat_limit}</span>
                      </span>
                      <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${s.seats_used / s.seat_limit > 0.8 ? 'bg-orange-500' : 'bg-emerald-500'}`}
                          style={{ width: `${Math.min(100, (s.seats_used / s.seat_limit) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackofficeLayout>
  );
}
